import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRandomWhoAmIQuestions } from '../utils/whoAmIQuestions';
import { playCorrectSound, playWrongSound } from '../utils/soundEffects';
import { X, Eye, HelpCircle } from 'lucide-react';
import confetti from 'canvas-confetti';

const INDIA_MAP_BG = 'https://images.unsplash.com/photo-1546833998-07256bcc76ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxJbmRpYSUyMG1hcCUyMGNvbG9yZnVsJTIwaWxsdXN0cmF0aW9ufGVufDB8fHx8MTc3NzA0NDIxMXww&ixlib=rb-4.1.0&q=85';

const OPTION_COLORS = [
  'bg-emerald-500 border-emerald-700 hover:bg-emerald-400',
  'bg-blue-500 border-blue-700 hover:bg-blue-400',
  'bg-purple-500 border-purple-700 hover:bg-purple-400',
  'bg-red-500 border-red-700 hover:bg-red-400',
];

export default function WhoAmIPlayPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [clueIndex, setClueIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const scoreRef = useRef(0);

  useEffect(() => { scoreRef.current = score; }, [score]);

  useEffect(() => {
    setQuestions(getRandomWhoAmIQuestions(10));
  }, []); // mount-only: generate once

  const currentQ = questions[currentIndex];

  const handleRevealClue = () => {
    if (clueIndex < currentQ.clues.length - 1) {
      setClueIndex((c) => c + 1);
    }
  };

  const handleOptionClick = (optionIdx) => {
    if (showAnswer) return;
    setSelectedOption(optionIdx);
    setShowAnswer(true);

    const isCorrect = optionIdx === currentQ.correctIndex;
    if (isCorrect) {
      setScore((s) => s + 1);
      playCorrectSound();
      confetti({ particleCount: 50, spread: 60, origin: { y: 0.7 } });
    } else {
      playWrongSound();
    }
  };

  const handleNext = () => {
    if (currentIndex + 1 >= questions.length) {
      navigate(`/who-am-i/results?score=${scoreRef.current}&total=${questions.length}`);
      return;
    }
    setCurrentIndex((i) => i + 1);
    setClueIndex(0);
    setSelectedOption(null);
    setShowAnswer(false);
  };

  if (!currentQ) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundImage: `url(${INDIA_MAP_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="glass-panel p-8"><p className="font-heading text-2xl text-amber-950">Loading...</p></div>
      </div>
    );
  }

  const visibleClues = currentQ.clues.slice(0, clueIndex + 1);
  const hasMoreClues = clueIndex < currentQ.clues.length - 1;

  return (
    <div
      className="min-h-screen flex flex-col p-3 sm:p-4"
      style={{ backgroundImage: `url(${INDIA_MAP_BG})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-7 h-7 text-amber-400" />
          <span data-testid="wai-score" className="font-heading text-3xl sm:text-4xl font-black text-amber-950">
            {score}/{questions.length}
          </span>
        </div>
        <span data-testid="wai-counter" className="font-body text-sm font-bold text-amber-900 bg-white/70 px-3 py-1 rounded-full">
          {currentIndex + 1}/{questions.length}
        </span>
        <button data-testid="wai-quit" onClick={() => navigate('/who-am-i')} className="bg-white/70 p-2 rounded-full hover:bg-white/90 transition-colors">
          <X className="w-5 h-5 text-amber-800" />
        </button>
      </div>

      {/* Clues area */}
      <div className="glass-panel p-5 sm:p-6 mb-4 flex-1">
        <p className="font-heading text-2xl sm:text-3xl font-black text-amber-950 text-center mb-5">
          Who Am I?
        </p>

        <div className="space-y-3 mb-5">
          {visibleClues.map((clue, i) => (
            <div
              key={i}
              data-testid={`wai-clue-${i}`}
              className="animate-pop-in flex items-start gap-3 bg-amber-50 rounded-2xl p-4 border-2 border-amber-200"
            >
              <span className="w-7 h-7 rounded-full bg-amber-400 text-white font-black text-sm flex items-center justify-center shrink-0 mt-0.5">
                {i + 1}
              </span>
              <p className="font-body text-base sm:text-lg text-amber-900 font-bold">{clue}</p>
            </div>
          ))}
        </div>

        {/* Reveal next clue button */}
        {!showAnswer && hasMoreClues && (
          <div className="text-center mb-4">
            <button
              data-testid="wai-next-clue"
              onClick={handleRevealClue}
              className="bg-amber-400 text-white font-bold text-sm py-2.5 px-6 rounded-full border-b-[4px] border-amber-600 active:border-b-0 active:translate-y-[4px] transition-all hover:bg-amber-300 inline-flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Show Next Clue ({clueIndex + 1}/{currentQ.clues.length})
            </button>
          </div>
        )}

        {/* Options */}
        {!showAnswer && (
          <div className="grid grid-cols-2 gap-3">
            {currentQ.options.map((opt, i) => {
              const optKey = `${currentQ.id}-opt-${opt}`;
              return (
              <button
                key={optKey}
                data-testid={`wai-option-${i}`}
                onClick={() => handleOptionClick(i)}
                className={`${OPTION_COLORS[i]} text-white font-bold text-sm sm:text-base py-4 px-4 rounded-2xl border-b-[5px] active:border-b-0 active:translate-y-[5px] transition-all shadow-md text-center`}
              >
                {opt}
              </button>
              );
            })}
          </div>
        )}

        {/* Answer feedback */}
        {showAnswer && (
          <div className="text-center animate-pop-in">
            {selectedOption === currentQ.correctIndex ? (
              <div className="mb-4">
                <p data-testid="wai-feedback-correct" className="font-heading text-2xl font-black text-emerald-600 mb-1">
                  Correct!
                </p>
                <p className="font-body text-lg text-amber-800">
                  It&apos;s the <span className="font-black text-amber-950">{currentQ.answer}</span>!
                </p>
              </div>
            ) : (
              <div className="mb-4">
                <p data-testid="wai-feedback-wrong" className="font-heading text-xl font-black text-red-500 mb-1">
                  Not quite!
                </p>
                <p className="font-body text-lg text-amber-800">
                  The answer is <span className="font-black text-amber-950">{currentQ.answer}</span>                </p>
              </div>
            )}
            <button
              data-testid="wai-next"
              onClick={handleNext}
              className="bg-orange-500 text-white font-black text-base py-3 px-8 rounded-full border-b-[4px] border-orange-700 active:border-b-0 active:translate-y-[4px] transition-all hover:bg-orange-400 inline-flex items-center gap-2"
            >
              {currentIndex + 1 >= questions.length ? 'See Results' : 'Next Animal'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
